import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { WORK_PHASE_RUNNING_LABELS, type CurrentWorkSession, type WorkPhase } from '@myapp/contract'
import { useStartAppointment } from '../../../entities/appointment'
import { useStartTask } from '../../../entities/task'
import { useCurrentWorkSession } from '../../../entities/schedule'
import { PATHS } from '../../../shared/config/paths'
import { useElapsedSince } from '../../../shared/hooks/useElapsedSince'
import { formatDurationDe } from '../../../shared/lib/durationDe'
import { EndAppointmentDepartureModal } from './EndAppointmentDepartureModal'
import { EndTaskDepartureModal } from './EndTaskDepartureModal'

/** The one label map every surface prints; the banner only ever shows an open session. */
const PHASE_LABELS: Record<WorkPhase, string> = {
  ...WORK_PHASE_RUNNING_LABELS,
  DONE: 'Abgeschlossen',
}

/**
 * The next tap for each open phase. ARBEIT sends him to the item instead: closing work needs the
 * sign-off sheet (Folgetermin, Kommentar, the one correction point), which does not belong in a
 * banner.
 */
const NEXT_ACTION_LABELS: Partial<Record<WorkPhase, string>> = {
  ANFAHRT: 'Arbeit starten',
  ARBEIT: 'Zum Termin',
  ABFAHRT: 'Abfahrt beenden',
}

/**
 * The live session banner on the worker home: current phase, object, ticking timer, and the next
 * phase action inline. Renders nothing while no session is open. The timer turns amber once the
 * measured time passes the planned minutes for the phase.
 */
export function WorkSessionBanner() {
  const { data: session } = useCurrentWorkSession()

  if (!session) return null
  return <BannerBody session={session} />
}

function BannerBody({ session }: { session: CurrentWorkSession }) {
  const navigate = useNavigate()
  const [showDeparture, setShowDeparture] = useState(false)
  const startAppointment = useStartAppointment()
  const startTask = useStartTask()

  const ageMs = useElapsedSince(new Date(session.phaseStartedAt).getTime())
  const elapsedMinutes = ageMs !== null ? Math.floor(ageMs / 60_000) : 0
  const overrun = session.plannedPhaseMinutes > 0 && elapsedMinutes > session.plannedPhaseMinutes

  const isAppointment = session.itemType === 'APPOINTMENT'
  const detailPath = isAppointment
    ? PATHS.workerAppointment.replace(':appointmentId', String(session.itemId))
    : PATHS.workerTask.replace(':taskId', String(session.itemId))

  const isStarting = startAppointment.isPending || startTask.isPending

  const onAction = () => {
    if (session.phase === 'ARBEIT') {
      navigate(detailPath)
      return
    }
    if (session.phase === 'ABFAHRT') {
      setShowDeparture(true)
      return
    }
    // ANFAHRT → "Arbeit starten" is a pure tap, so it fires straight from the banner. A failure
    // surfaces on the item page's action bar, which owns the retry state.
    if (isAppointment) {
      startAppointment.mutate({
        id: session.itemId,
        workOrderId: session.workOrderId ?? undefined,
      })
    } else {
      startTask.mutate({ id: session.itemId })
    }
  }

  const actionLabel = NEXT_ACTION_LABELS[session.phase]
  const label = isAppointment ? 'Termin' : 'Aufgabe'

  return (
    <section
      aria-label={`Laufende Sitzung: ${PHASE_LABELS[session.phase]}`}
      className="border-b border-green-200 bg-green-50 px-4 py-3"
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(detailPath)}
          className="flex min-w-0 flex-1 flex-col items-start text-left"
        >
          <span className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-green-500 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" aria-hidden="true" />
              {PHASE_LABELS[session.phase]}
            </span>
            <span
              className={[
                'text-sm font-medium tabular-nums',
                overrun ? 'text-amber-600' : 'text-green-700',
              ].join(' ')}
            >
              {formatDurationDe(elapsedMinutes)}
              {session.plannedPhaseMinutes > 0 && (
                <span className="font-normal opacity-70">
                  {' '}
                  / {formatDurationDe(session.plannedPhaseMinutes)}
                </span>
              )}
            </span>
          </span>
          <span className="mt-0.5 max-w-full truncate text-sm text-slate-700">
            {label} · {session.title}
          </span>
        </button>

        {actionLabel && (
          <button
            type="button"
            onClick={onAction}
            disabled={isStarting}
            className="shrink-0 rounded-lg bg-green-500 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {actionLabel}
          </button>
        )}
      </div>

      {isAppointment ? (
        <EndAppointmentDepartureModal
          show={showDeparture}
          setShow={setShowDeparture}
          appointmentId={session.itemId}
          workOrderId={session.workOrderId ?? undefined}
          actualEndsAt={session.phaseStartedAt}
          onSuccess={() => setShowDeparture(false)}
        />
      ) : (
        <EndTaskDepartureModal
          show={showDeparture}
          setShow={setShowDeparture}
          taskId={session.itemId}
          actualEndsAt={session.phaseStartedAt}
          onSuccess={() => setShowDeparture(false)}
        />
      )}
    </section>
  )
}
